import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { MapPin, Search, ArrowRight, Loader2, X, Plus, Sparkles, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { funnelTrack } from "@/lib/funnel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatVolume(vol) {
  if (!vol || vol <= 0) return null;
  if (vol >= 1000000) return `${(vol / 1000000).toFixed(1).replace(".0", "")}M`;
  if (vol >= 10000) return `${(vol / 1000).toFixed(0)}K`;
  if (vol >= 1000) return `${(vol / 1000).toFixed(1).replace(".0", "")}K`;
  return vol.toLocaleString();
}

/**
 * KeywordPicker — reusable component
 * mode="pre-audit": used on AuditPage before audit generation
 * mode="post-audit": used in Checklist widget after audit exists
 */
export function KeywordPicker({
  mode = "pre-audit",
  placeId,
  auditId,
  businessName,
  businessType,
  address,
  city,
  initialKeywords = [],
  onConfirm,
  saving = false,
}) {
  const [suggestions, setSuggestions] = useState(null);
  const [selected, setSelected] = useState(initialKeywords);
  const [loading, setLoading] = useState(true);
  const [customInput, setCustomInput] = useState("");
  const [showCustom, setShowCustom] = useState(false);
  const [customCount, setCustomCount] = useState(0);
  const [shakeId, setShakeId] = useState(null);

  const fetchSuggestions = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = mode === "pre-audit"
        ? `${API}/audit/suggest-keywords`
        : `${API}/checklist/${auditId}/suggest-keywords`;
      const body = mode === "pre-audit"
        ? { place_id: placeId, business_name: businessName, business_type: businessType, address, city }
        : undefined;
      const res = mode === "pre-audit"
        ? await axios.post(endpoint, body)
        : await axios.get(endpoint, { headers: { Authorization: `Bearer ${localStorage.getItem("rankify-token")}` } });
      setSuggestions(res.data);
      if (initialKeywords.length === 0 && res.data.recommended_selection) {
        setSelected(res.data.recommended_selection.slice(0, 3));
      }
      funnelTrack("funnel.keywords_loaded", {
        place_id: placeId,
        business_name: businessName,
        step: "2",
        metadata: { keyword_count: (res.data.keywords || []).length },
      });
    } catch {
      setSuggestions(null);
    } finally {
      setLoading(false);
    }
  }, [mode, placeId, auditId, businessName, businessType, address, city]);

  useEffect(() => { fetchSuggestions(); }, [fetchSuggestions]);

  // Build chip list: prefer enriched keywords array, fallback to flat arrays
  const enrichedKeywords = suggestions?.keywords || [];
  const allChips = enrichedKeywords.length > 0
    ? enrichedKeywords.map(k => k.keyword)
    : [...(suggestions?.primary_categories || []), ...(suggestions?.secondary_categories || []), ...(suggestions?.local_keywords || [])];

  // Volume lookup
  const volumeMap = {};
  enrichedKeywords.forEach(k => { volumeMap[k.keyword] = k.volume || 0; });

  const recommended = new Set(suggestions?.recommended_selection || []);

  const toggleChip = (chip) => {
    if (selected.includes(chip)) {
      setSelected(selected.filter(k => k !== chip));
    } else if (selected.length >= 5) {
      setShakeId(chip);
      setTimeout(() => setShakeId(null), 500);
      toast.error("Maximum 5 keywords selected");
    } else {
      setSelected([...selected, chip]);
    }
  };

  const addCustom = () => {
    const val = customInput.trim();
    if (!val) return;
    if (customCount >= 2) { toast.error("Maximum 2 custom keywords allowed"); return; }
    if (selected.includes(val)) { toast.error("Already selected"); return; }
    if (selected.length >= 5) { toast.error("Maximum 5 keywords selected"); return; }
    setSelected([...selected, val]);
    setCustomCount(c => c + 1);
    setCustomInput("");
    setShowCustom(false);
  };

  // Calculate total volume for selected keywords
  const selectedVolume = selected.reduce((sum, kw) => sum + (volumeMap[kw] || 0), 0);
  const hasVolumeData = enrichedKeywords.some(k => k.volume > 0);

  if (loading) {
    return (
      <div className="space-y-4 py-4" data-testid="keyword-picker-loading">
        <div className="flex flex-col items-center text-center py-6">
          <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center mb-4">
            <Sparkles className="w-6 h-6 text-indigo-500 animate-pulse" />
          </div>
          <p className="text-base font-semibold text-[#111827] mb-1">Analyzing your industry keywords...</p>
          <p className="text-sm text-[#6B7280] max-w-xs">Our AI is finding the highest-value search terms for your business. This takes 10-20 seconds.</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-9 rounded-full bg-[#F3F4F6] animate-pulse" style={{ animationDelay: `${i * 150}ms` }} />
          ))}
        </div>
      </div>
    );
  }

  if (!suggestions) {
    return (
      <div className="space-y-3" data-testid="keyword-picker-fallback">
        <p className="text-sm text-[#6B7280]">Enter keywords manually:</p>
        <Input placeholder="e.g. Auto Repair Toronto, Cash for Cars Calgary" value={customInput}
          onChange={e => setCustomInput(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") addCustom(); }}
          className="bg-white border-[#E5E7EB] text-[#111827]" />
        {selected.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {selected.map((kw, i) => (
              <Badge key={i} className="bg-[#EEF2FF] text-[#3B5BDB] border-[#3B5BDB]/20 gap-1 pr-1">
                {kw}<button onClick={() => setSelected(selected.filter(k => k !== kw))}><X className="w-3 h-3" /></button>
              </Badge>
            ))}
          </div>
        )}
        <Button onClick={() => onConfirm?.(selected)} disabled={saving || selected.length < 3}
          className="w-full bg-[#3B5BDB] hover:bg-[#2F4AC7] text-white h-12">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          {mode === "pre-audit" ? "Generate My Free Report" : "Save Keywords"} ({selected.length}/5)
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="keyword-picker">
      {suggestions.industry_label && (
        <p className="text-sm text-[#6B7280]">
          {hasVolumeData ? (
            <>Sorted by <span className="font-medium text-[#111827]">monthly Google searches</span>{city ? ` in ${city}` : ""}. Pick 3-5 that match:</>
          ) : (
            <>AI analyzed your <span className="font-medium text-[#111827]">{suggestions.industry_label}</span> business. Pick 3-5 that fit best:</>
          )}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {allChips.map((chip, i) => {
          const isSelected = selected.includes(chip);
          const isRecommended = recommended.has(chip);
          const volume = volumeMap[chip] || 0;
          const volStr = formatVolume(volume);
          const isHighVolume = volume >= 5000;
          return (
            <motion.button
              key={chip}
              onClick={() => toggleChip(chip)}
              animate={shakeId === chip ? { x: [-4, 4, -4, 4, 0] } : {}}
              transition={{ duration: 0.3 }}
              className={`relative inline-flex items-center gap-1.5 py-2 px-4 rounded-full border text-sm transition-all ${
                isSelected
                  ? "bg-[#EEF2FF] border-[#3B5BDB] text-[#3B5BDB] font-medium"
                  : isHighVolume
                    ? "bg-white border-[#c7d2fe] text-[#374151] hover:border-[#3B5BDB]"
                    : "bg-white border-[#E5E7EB] text-[#374151] hover:border-[#3B5BDB]"
              }`}
              data-testid={`keyword-chip-${i}`}
            >
              {isRecommended && !isSelected && (
                <Sparkles className="absolute -top-1.5 -right-1 w-3 h-3 text-amber-400" />
              )}
              <span>{chip}</span>
              {volStr && (
                <span
                  data-testid={`chip-volume-${i}`}
                  className={`text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${
                    isSelected
                      ? "bg-[#c7d2fe] text-[#4338ca]"
                      : "bg-[#f0f0ff] text-[#6366f1]"
                  }`}
                >
                  {volStr}/mo
                </span>
              )}
            </motion.button>
          );
        })}
      </div>

      {/* Total traffic summary for selected keywords */}
      {hasVolumeData && selected.length > 0 && selectedVolume > 0 && (
        <div
          data-testid="selected-traffic-summary"
          className="flex items-center justify-center gap-2 py-2.5 px-4 bg-[#f8fafc] rounded-lg text-sm text-[#475569]"
        >
          <TrendingUp className="w-4 h-4 text-[#3B5BDB]" />
          <span>
            <strong className="text-[#3B5BDB] text-base">{selectedVolume.toLocaleString()}</strong> people search for these terms every month{city ? ` in ${city}` : ""}
          </span>
        </div>
      )}

      {/* Custom keyword input */}
      {showCustom ? (
        <div className="flex gap-2">
          <Input placeholder="Add your own keyword..." value={customInput} onChange={e => setCustomInput(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") addCustom(); }}
            className="bg-white border-[#E5E7EB] text-[#111827] flex-1" autoFocus data-testid="custom-keyword-input" />
          <Button variant="outline" size="sm" onClick={addCustom} className="border-[#E5E7EB]"><Plus className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" onClick={() => setShowCustom(false)}><X className="w-4 h-4" /></Button>
        </div>
      ) : customCount < 2 ? (
        <button onClick={() => setShowCustom(true)} className="text-sm text-[#3B5BDB] hover:underline" data-testid="add-custom-btn">
          + Add your own keyword
        </button>
      ) : null}

      {/* Show custom keywords as badges */}
      {selected.filter(kw => !allChips.includes(kw)).length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="custom-keywords-display">
          <span className="text-xs text-[#6B7280] self-center mr-1">Your keywords:</span>
          {selected.filter(kw => !allChips.includes(kw)).map((kw, i) => (
            <Badge key={i} className="bg-[#EEF2FF] text-[#3B5BDB] border-[#3B5BDB]/20 gap-1 pr-1">
              {kw}
              <button onClick={() => { setSelected(selected.filter(k => k !== kw)); setCustomCount(c => Math.max(0, c - 1)); }}>
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Selection counter */}
      <div className={`text-sm font-medium ${selected.length < 3 ? "text-[#DC2626]" : "text-[#059669]"}`} data-testid="keyword-count">
        Selected: {selected.length}/5 {selected.length < 3 && "(minimum 3 required)"}
      </div>

      <Button onClick={() => onConfirm?.(selected)} disabled={saving || selected.length < 3}
        className="w-full bg-[#3B5BDB] hover:bg-[#2F4AC7] text-white h-12 text-base font-semibold" data-testid="keyword-confirm-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
        {mode === "pre-audit" ? "Generate My Free Report" : "Save Keywords"}
      </Button>
    </div>
  );
}
